import type { WebContainer } from '@webcontainer/api';
import { atom, computed, type ReadableAtom } from 'nanostores';
import { useCallback, useEffect, useState } from 'react';
import { clientFetch } from '~/lib/.client/fetch';
import { addBreadcrumb as appsignalAddBreadcrumb, sendAppSignalError, type Breadcrumb } from '~/lib/appsignal.client';
import { resetExplicitBranch, updateGitignore, useGithubToken, type GetGithubToken } from '~/lib/client-github';
import { CloneStateKind, useGithubStore } from '~/lib/context/github';
import { getExplicitBranch } from '~/lib/github';
import { commitFiles, createFirstCommit, getReference, getTarball, GraphQLError } from '~/lib/github-api';
import type { FileEntry } from '~/types/git';
import type { ProjectGithubIntegration, Target } from '~/types/github';
import type { DirectoryEntry as WebcontainerDirectoryEntry } from '~/types/webcontainer';
import { toBase64 } from '~/utils/base64';
import { ClientError } from '~/utils/client-error';
import { WORK_DIR } from '~/utils/constants';
import { debounce } from '~/utils/debounce';
import { fromLatin1 } from '~/utils/encoding';
import { EventEmitter } from '~/utils/event-emitter';
import { useInitRef } from '~/utils/init-ref';
import { invariant } from '~/utils/invariant';
import { isBinaryFile } from '~/utils/is-binary-file';
import { createScopedLogger } from '~/utils/logger';
import { waitForFirstValue, waitUntil } from '~/utils/nanostore';
import { NOOP } from '~/utils/noops';
import { wait, withResolvers } from '~/utils/promises';
import { iterableFromReadable, streamDone } from '~/utils/stream';
import { filesFromTarball } from '~/utils/tarball';
import { unreachable } from '~/utils/unreachable';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
const logger = createScopedLogger('github');

const POLL_INTERVAL = 30_000;

interface DiffResult<T> {
  additions: Array<FileEntry<T>>;
  deletions: Array<{ path: string }>;
  newFiles: Set<string>;

  // only present for `inPlace: false`
  readonly files: Map<string, string>;
}

interface HookInit {
  webcontainer: Promise<WebContainer>;

  // waits for the projectId to be defined
  projectId(): Promise<string | undefined>;

  fs: {
    ready: () => Promise<void>;
    writeFile(path: string, contents: string | Uint8Array): Promise<void>;
    readFile(path: string): string | Uint8Array | undefined;
    removeFile(path: string): Promise<void>;
  };
}

const WATCH_PATHS_DEBOUNCING = 500;

export type GithubIntegrationInitialState =
  | {
      // the project is not linked to a GH repo, we're waiting for the user to do it
      mode: 'link';
    }
  | {
      // there is an import in progress
      mode: 'import';
    }
  | ({
      // a linked project was open, with repo details readily available
      mode: 'project';
    } & ProjectGithubIntegration);

export function useGithubIntegration(
  init: HookInit,
  initialState: GithubIntegrationInitialState,
  ignoreWrites: boolean,
) {
  const ignoreWritesStoreRef = useInitRef(() => atom(false));

  const ignoreWritesStore = ignoreWritesStoreRef.current;

  const github = useGithubStore();
  const initialTarget = initialState.mode === 'project' ? initialState : undefined;

  useEffect(() => {
    ignoreWritesStore.set(ignoreWrites);
  }, [ignoreWrites]);

  const [hasExplicitBranch] = useState(() => {
    const url = new URL(window.location.href);

    return getExplicitBranch(url.searchParams) != null;
  });

  const getGithubToken = useGithubToken();

  const integrationRef = useInitRef(
    () =>
      new GithubIntegration(
        {
          webcontainer: init.webcontainer,
          fs: init.fs,
          getGithubToken,
        },
        ignoreWritesStore,
        initialTarget,
      ),
  );

  const integration = integrationRef.current;

  useEffect(() => {
    const ctrl = new AbortController();

    const debouncedCommit = debounce(
      (version: number) => integration.commit(undefined, { version }),
      WATCH_PATHS_DEBOUNCING,
    );

    function watchFiles(webcontainer: WebContainer) {
      // FIXME: use gitignore
      return webcontainer.internal.watchPaths(
        {
          path: WORK_DIR,
          includeContent: false,
          exclude: ['node_modules/**'],
        },
        () => {
          if (ignoreWritesStore.get()) {
            return;
          }

          const newVersion = integration.updateWrite();

          debouncedCommit(newVersion);
        },
      );
    }

    async function initialize() {
      addBreadcrumb({
        action: 'useGithubIntegration.initialize',
        metadata: {
          mode: initialState.mode,
          ...(initialTarget
            ? { repository: `${initialTarget.repository.owner}/${initialTarget.repository.name}` }
            : {}),
        },
      });

      if (hasExplicitBranch) {
        resetExplicitBranch();
      }

      const targetPromise = initialTarget ?? waitForFirstValue(github.target);

      const ready = Promise.all([
        init.webcontainer.catch(NOOP as () => void),
        targetPromise,
        init.projectId().catch(NOOP as () => void),
        github.connected(),
      ]);

      const aborted = new Promise<void>((resolve) => {
        ctrl.signal.addEventListener('abort', () => resolve());
      });

      const result = await Promise.race([ready, aborted]);

      if (!result) {
        return;
      }

      const [webcontainer, target, projectId] = result;

      if (!projectId || !webcontainer) {
        return;
      }

      const newRepo = initialState.mode === 'link' || (initialState.mode === 'project' && !initialState.initialized);
      const needsClone = initialState.mode === 'project' && initialState.initialized;

      const actuallyNeedsClone = github.state.get().clone.kind === CloneStateKind.Pending;

      if (needsClone !== actuallyNeedsClone) {
        sendAppSignalError(new GithubCloneError(JSON.stringify(initialState)));
      }

      if (!github.connection.get().account) {
        const message = 'Unable to sync with repository: no GitHub credentials';

        // this is most likely true, as it's hard to imagine credentials being lost right after triggering an import
        github.setSyncStatus({ clone: { kind: CloneStateKind.Error, message } });

        throw new Error(message, { cause: { error: true } });
      }

      if (hasExplicitBranch) {
        updateGithubIntegration(projectId, { branchName: target.branchName });
      }

      const stopLinked = integration.on('linked', () => {
        updateGithubIntegration(projectId, { initialized: true });
      });

      try {
        await integration.init(target, {
          newRepo,
        });
      } catch (error) {
        github.setSyncStatus({ clone: { kind: CloneStateKind.Error } });

        addBreadcrumb({
          action: 'GithubIntegration.(cloned)',
          metadata: {
            error: true,
          },
        });

        throw error;
      }

      github.setSyncStatus({ clone: { kind: CloneStateKind.Done } });

      addBreadcrumb({
        action: 'GithubIntegration.(cloned)',
        metadata: {
          error: false,
        },
      });

      if (newRepo && !ignoreWritesStore.get()) {
        integration.commit('Start repository');
      }

      const stopWatchers = watchFiles(webcontainer);

      if (initialState.mode === 'import') {
        const gitignore = init.fs.readFile('.gitignore');
        const gitignoreSource = typeof gitignore === 'string' ? gitignore : textDecoder.decode(gitignore);

        const newGitignore = gitignore ? updateGitignore(gitignoreSource) : DEFAULT_GITIGNORE;

        init.fs.writeFile('.gitignore', newGitignore);
      }

      ctrl.signal.addEventListener('abort', () => {
        stopWatchers();
        stopLinked();
      });
    }

    initialize().catch((error) => {
      logger.error(error);

      if (error instanceof GraphQLError && error.errors.length > 0) {
        integration.dispatch('error', [error.errors.at(0)!.message, error]);
      } else if (error instanceof ClientError) {
        integration.dispatch('error', [error.message]);
      } else {
        integration.dispatch('error', ['Error while syncing with GitHub', error]);
      }
    });

    const unsubscribe = integration.busy.listen((busy) => {
      github.setSyncStatus({ status: busy ? 'pending' : 'success' });
    });

    return () => {
      ctrl.abort();
      integration.destroy();
      unsubscribe();
    };
  }, []);

  const commit = useCallback((message?: string) => {
    return integration.commit(message);
  }, []);

  const pull = useCallback(() => {
    return integration.pull();
  }, []);

  const connected = useCallback(() => {
    return integration.ready();
  }, []);

  const maybePull = useCallback(() => {
    return integration.maybePull();
  }, []);

  if (import.meta.env.DEV) {
    Object.assign(window, { githubIntegration: integration });
  }

  return {
    commit,
    pull,
    maybePull,
    connected,
    events: integration as EventEmitter<GithubIntegrationEvents>,
    get diff() {
      return integration.diff;
    },
  };
}

/**
 * The 'precommit' status means that we are checking if we need to commit, but the answer might be no.
 * We use it internally to block events, but externally is not shown as being "busy".
 */
type QueueStatus = 'idle' | 'precommit' | 'committing' | 'conflict';

interface GithubIntegrationEvents {
  update: [string, Promise<void>];
  error: [string] | [string, Error];
  conflict: [];
}

interface GithubIntegrationPrivateEvents extends GithubIntegrationEvents {
  linked: [];
  updateAttempt: [];
}

class GithubIntegration extends EventEmitter<GithubIntegrationPrivateEvents> {
  #differ: Differ;
  #githubSync: GithubSync;
  #getGithubToken: GetGithubToken;
  #queueStatusStore = atom<QueueStatus>('idle');

  // queue of pending commit attempts
  #queue: Array<{ message?: string; version?: number }> = [];

  // a version counter to keep track of user edits
  #pendingWrite = atom(0);

  // if this flag is true, syncs should be interrupted
  #queueActive = computed(
    [this.#pendingWrite, this.#queueStatusStore],
    (version, status) => version > 0 || status !== 'idle',
  );

  // we sometimes want to manually _pause_ syncs (different from interrupting them)
  #syncPaused = atom(false);

  #fs: HookInit['fs'];
  #cleanup: () => void;

  #firstPull?: Promise<ReadableStream<FileEntry> | undefined>;

  target?: Target;

  oid?: string;

  // for public consumption, we show our "busy" status less often
  busy = computed(
    [this.#pendingWrite, this.#queueStatusStore],
    (version, status) => version > 0 || status === 'committing',
  );

  // true when we had to overwrite the project contents
  diff = false;

  constructor(
    init: {
      webcontainer: Promise<WebContainer>;
      fs: HookInit['fs'];
      getGithubToken: GetGithubToken;
    },
    paused: ReadableAtom<boolean>,
    state?: ProjectGithubIntegration,
  ) {
    super();

    this.#fs = init.fs;
    this.#getGithubToken = init.getGithubToken;

    this.target = state?.initialized
      ? {
          repository: state.repository,
          branchName: state.branchName,
        }
      : undefined;

    if (this.target) {
      this.#firstPull = this.#pull();

      // this is awaited later
      this.#firstPull.catch(NOOP);
    }

    this.#differ = new Differ(init.webcontainer, init.fs);

    const syncInterrupted = computed([paused, this.#queueActive], (paused, queueActive) => paused || queueActive);

    this.#githubSync = new GithubSync(syncInterrupted, this.#syncPaused, this.#getGithubToken);

    this.#cleanup = this.#githubSync.on('update', (event) => {
      if (import.meta.env.DEV && !['conflict', 'idle'].includes(this.#queueStatus)) {
        unreachable('queue should not be active when we trigger an update');
      }

      const [oids, contents] = event.detail;

      if (oids && contents) {
        this.#syncPaused.set(true);
        this.#dispatchRemoteUpdate(oids, contents);
      } else {
        this.dispatch('updateAttempt', []);
        this.#resumeQueueAfterUpdate();
      }
    });
  }

  get #queueStatus() {
    return this.#queueStatusStore.get();
  }

  async #pull() {
    if (!this.target) {
      unreachable('guarded');
    }

    const token = await this.#getGithubToken();

    if (!token) {
      return undefined;
    }

    const ref = await getReference(this.target, token);

    const oid = ref?.target?.oid;

    if (!oid) {
      throw new ClientError(`Branch ${this.target.branchName} does not exist`);
    }

    this.oid = oid;

    return filesFromTarball(getTarball(this.target, oid, token));
  }

  updateWrite() {
    const nextVersion = this.#pendingWrite.get() + 1;

    this.#pendingWrite.set(nextVersion);

    return nextVersion;
  }

  destroy() {
    this.#cleanup();
    this.#githubSync.abort();
  }

  ready() {
    return this.target != null;
  }

  async init(target: Target, { newRepo }: { newRepo: boolean }) {
    addBreadcrumb({
      action: 'GithubIntegration.init',
      metadata: {
        newRepo,
      },
    });

    this.target = target;

    if (newRepo) {
      this.#differ.initEmpty();
    } else {
      this.#firstPull ??= this.#pull();

      addBreadcrumb({
        action: 'GithubIntegration.init(contents)',
        metadata: {
          firstPull: Boolean(this.#firstPull),
        },
      });

      const firstPull = await this.#firstPull;

      addBreadcrumb({
        action: 'GithubIntegration.init(diff)',
      });

      await this.#fs.ready();

      await this.#differ.initFull(this.target);

      if (firstPull) {
        this.diff = await this.#firstDiff(firstPull);
      }
    }

    if (this.oid) {
      // for existing repos, we fast-track the sync loop to ensure a quick first pull
      this.#githubSync.start(this.oid, this.target);
    }
  }

  async pull() {
    const controller = new AbortController();
    const emitter = this as EventEmitter<GithubIntegrationPrivateEvents>;
    const { signal } = controller;

    const promise = Promise.race([
      EventEmitter.once(emitter, 'update', { signal }),
      EventEmitter.once(emitter, 'updateAttempt', { signal }),
    ]);

    this.#githubSync.pullAndDispatch(this.target!, false).catch((error) => {
      logger.error('forced pull', error);
    });

    await promise;
  }

  maybePull() {
    this.#githubSync.skipWait();
  }

  async commit(message?: string, { force = false, version = 0 } = {}) {
    if (!this.target) {
      unreachable('should be guarded by consumers');
    }

    if (this.#queueStatus === 'conflict' || (this.#queueStatus !== 'idle' && !force)) {
      logger.debug('deferring commit');
      this.#queue.push({ message, version });

      return;
    }

    logger.debug('commit, message:', JSON.stringify(message ?? null));

    this.#queueStatusStore.set('precommit');

    try {
      const shouldInit = this.oid == null;
      const fileChanges = await this.#differ.diff(this.target, shouldInit);

      if (!fileChanges) {
        logger.debug('commit, no changes');

        return;
      }

      this.#queueStatusStore.set('committing');

      const nextOid = await commitChanges(
        this.#getGithubToken,
        { fileChanges, commitMessage: message },
        this.target,
        this.oid,
      );

      if (nextOid == null) {
        this.dispatch('conflict', []);
        this.#queueStatusStore.set('conflict');

        return;
      }

      this.oid = nextOid;

      if (shouldInit) {
        this.#githubSync.start(nextOid, this.target);
        this.dispatch('linked', []);
      } else {
        this.#githubSync.update(nextOid);
      }
    } finally {
      this.#continueProcessingQueue(version);
    }
  }

  #continueProcessingQueue(version: number) {
    if (this.#queueStatus === 'conflict') {
      return;
    }

    if (this.#processNextInQueue().empty) {
      this.#queueStatusStore.set('idle');

      if (version > 0 && version === this.#pendingWrite.get()) {
        this.#pendingWrite.set(0);
      }
    }
  }

  #resumeQueueAfterUpdate() {
    this.#syncPaused.set(false);

    if (this.#queueStatus === 'conflict') {
      this.#queueStatusStore.set('idle');
    }

    if (this.#processNextInQueue().empty) {
      this.#queueStatusStore.set('idle');
    }
  }

  #processNextInQueue() {
    if (this.#queue.length > 0) {
      const item = this.#queue.shift()!;

      this.commit(item.message, { version: item.version, force: true });

      return { empty: false };
    }

    return { empty: true };
  }

  async #firstDiff(contents: ReadableStream<FileEntry>) {
    addBreadcrumb({
      action: 'GithubIntegration.firstDiff',
    });

    if (!this.target && import.meta.env.DEV) {
      unreachable('should not happen');
    }

    const target = this.target!;

    const result = await this.#differ.diffWith(target, contents);

    if (!result.diff) {
      return false;
    }

    this.#differ.overwrite(result.diff.files);

    const sync = this.#apply(result.diff);

    queueMicrotask(() => {
      const diff = result.diff!;

      const binaryModifications = diff.additions.filter(
        (entry) => !diff.newFiles.has(entry.path) && isBinaryFile(entry.contents),
      ).length;

      addBreadcrumb({
        action: 'GithubIntegration.firstDiff(sync)',
        metadata: {
          additions: diff.additions.length,
          deletions: diff.deletions.length,
          newFiles: diff.newFiles.size,
          binaryModifications,
        },
      });
    });

    await sync;

    return true;
  }

  async #dispatchRemoteUpdate(
    { current: oid, previous: previousOid }: { current: string; previous: string },
    contents: ReadableStream<FileEntry | null>,
  ) {
    if (!this.target && import.meta.env.DEV) {
      unreachable('should not happen');
    }

    const target = this.target!;

    const result = await this.#differ.diffWith(target, contents);

    const interruptible = this.#queueStatus !== 'conflict';

    try {
      if (interruptible && (result.cancelled || this.#githubSync.interrupted.get())) {
        logger.debug('pull diff with no effect, canceled: ', result.cancelled);

        this.dispatch('updateAttempt', []);

        return;
      }

      // block the queue while we apply
      this.#queueStatusStore.set('committing');

      // the case where `result.diff == null` is legit: a remote update might change the oid but not the contents
      if (result.diff) {
        this.#differ.overwrite(result.diff.files);
      }

      if (import.meta.env.DEV && this.oid !== previousOid) {
        unreachable('syncs should be interrupted whenever the oid changes');
      }

      const sync = result.diff ? this.#apply(result.diff) : Promise.resolve();

      this.dispatch('update', [oid, sync]);

      await sync;

      this.#githubSync.update(oid);

      this.oid = oid;
    } finally {
      this.#resumeQueueAfterUpdate();
    }
  }

  async #apply(diff: DiffResult<Uint8Array>) {
    const additions = diff.additions.map(async (addition) => {
      const isBinaryModification = !diff.newFiles.has(addition.path) && isBinaryFile(addition.contents);

      if (isBinaryModification) {
        await this.#fs.removeFile(addition.path);
      }

      return await this.#fs.writeFile(addition.path, addition.contents);
    });

    const deletions = diff.deletions.map((deletion) => this.#fs.removeFile(deletion.path));

    await Promise.all([...additions, ...deletions]);
  }
}

type DiffMode = 'init' | 'in-place' | 'copy';

const DEFAULT_GITIGNORE = 'node_modules/\n.env\n';

class Differ {
  #webcontainer: Promise<WebContainer>;
  #init = withResolvers<void>();
  #fs: HookInit['fs'];
  #locked = false;

  // path -> sha-1 checksum
  #files: Map<string, string> = new Map();

  constructor(webcontainer: Promise<WebContainer>, fs: HookInit['fs']) {
    this.#webcontainer = webcontainer;
    this.#fs = fs;
  }

  async initFull(target: Target) {
    await this.#diffWithFileSystem(target).then(NOOP);
    this.#init.resolve();
  }

  initEmpty() {
    this.#init.resolve();
  }

  async diff(target: Target, init?: boolean) {
    await this.#init.promise;

    return await this.#diffWithFileSystem(target, init);
  }

  overwrite(files: Map<string, string>) {
    this.#files = files;
  }

  async diffWith(target: Target, contents: ReadableStream<FileEntry | null>) {
    await this.#init.promise;

    let cancelled = false;

    const withoutCancel = new TransformStream<FileEntry | null, FileEntry>({
      transform(chunk, controller) {
        if (chunk == null) {
          cancelled = true;
        } else if (!cancelled) {
          controller.enqueue(chunk);
        }
      },
    });

    contents.pipeTo(withoutCancel.writable).catch(NOOP);

    const result = await this.#diff(target, iterableFromReadable(withoutCancel.readable), {
      // we can cancel the diff with no side effects to the stored files
      mode: 'copy',
      base64: false,
    });

    if (cancelled) {
      return { cancelled: true };
    }

    return { diff: result, cancelled: false };
  }

  async #diffWithFileSystem(target: Target, init?: boolean) {
    const webcontainer = await this.#webcontainer;

    addBreadcrumb({
      action: 'diffWithFileSystem(serialize)',
    });

    const exportPromise = webcontainer.internal.serialize('.', { useGitignore: true });

    let rawExported = await Promise.race([wait(15_000), exportPromise]);
    let reported = false;

    if (rawExported == null) {
      reported = true;
      sendAppSignalError(new GithubCloneError('Serialized timed out'));
    }

    rawExported ??= await exportPromise;

    if (reported) {
      sendAppSignalError(new SerializeMisreportError());
    }

    const exported: WebcontainerDirectoryEntry = JSON.parse(new TextDecoder().decode(rawExported));

    function* fsTreeIter() {
      const queue = Object.entries(exported.d);

      while (queue.length > 0) {
        const [path, entry] = queue.shift()!;

        if ('d' in entry) {
          queue.push(...Object.entries(entry.d).map(([entryPath, entry]) => [`${path}/${entryPath}`, entry] as any));

          continue;
        }

        if ('l' in entry.f) {
          continue;
        }

        const buffer = encode(entry.f.c, entry.f.b);

        yield { path, contents: buffer };
      }
    }

    return this.#diff(target, fsTreeIter(), { mode: init ? 'init' : 'in-place' });
  }

  async #diff(
    target: Target,
    tree: MaybeAsyncIterable<FileEntry>,
    options?: { base64?: true; mode?: DiffMode },
  ): Promise<DiffResult<string> | undefined>;
  async #diff(
    target: Target,
    tree: MaybeAsyncIterable<FileEntry>,
    options: { base64: false; mode?: DiffMode },
  ): Promise<DiffResult<Uint8Array> | undefined>;
  async #diff(
    target: Target,
    tree: MaybeAsyncIterable<FileEntry>,
    { base64: asBase64 = true, mode = 'in-place' }: { base64?: boolean; mode?: DiffMode } = {},
  ) {
    addBreadcrumb({
      action: 'Differ#diff',
      metadata: {
        mode,
      },
    });

    const inPlace = mode !== 'copy';

    if (import.meta.env.DEV && this.#locked) {
      unreachable('diff() should never be contended');
    }

    if (inPlace) {
      this.#locked = true;
    }

    const additions: Array<FileEntry<string | Uint8Array>> = [];
    const deletions: Array<{ path: string }> = [];
    const newFiles = new Set<string>();

    const files = inPlace ? this.#files : new Map(this.#files);

    const previousFiles = new Set(files.keys());
    const pending = [];

    const compare = (path: string, contents: Uint8Array, binaryDigest: ArrayBuffer, existingDigest?: string) => {
      const digest = toBase64(new Uint8Array(binaryDigest));

      if (!existingDigest || existingDigest !== digest) {
        additions.push({ path, contents: asBase64 ? toBase64(contents) : contents });

        if (!existingDigest) {
          newFiles.add(path);
        }

        files.set(path, digest);
      }

      previousFiles.delete(path);
    };

    for await (const { path, contents } of tree) {
      const existing = files.get(path);

      const promise = crypto.subtle
        .digest('SHA-1', contents)
        .then((binaryDigest) => compare(path, contents, binaryDigest, existing));

      pending.push(promise);
    }

    await Promise.all(pending);

    for (const extraneous of previousFiles) {
      deletions.push({ path: extraneous });

      files.delete(extraneous);
    }

    if (mode === 'init') {
      const extraFiles = await this.#initializeRepo(target);

      for (const extra of extraFiles) {
        additions.push(extra);
        newFiles.add(extra.path);
      }
    }

    if (inPlace) {
      this.#locked = false;
    }

    if (additions.length === 0 && deletions.length === 0) {
      return undefined;
    }

    return { additions, deletions, newFiles, files: inPlace ? new Map() : files };
  }

  async #initializeRepo(target: Target) {
    const files = [
      { path: '.gitignore', contents: DEFAULT_GITIGNORE },
      { path: 'README.md', contents: `${target.repository.name}\n` },
    ].filter(({ path }) => !this.#files.has(path));

    if (files.length === 0) {
      return [];
    }

    const additions: Array<FileEntry<string | Uint8Array>> = [];

    for (const file of files) {
      const contents = textEncoder.encode(file.contents);

      const digest = await crypto.subtle.digest('SHA-1', contents);

      this.#files.set(file.path, toBase64(new Uint8Array(digest)));
      this.#fs.writeFile(file.path, file.contents);

      additions.push({ path: file.path, contents: toBase64(contents) });
    }

    return additions;
  }
}

interface GithubSyncEvents {
  update: [{ current: string; previous: string }, ReadableStream<FileEntry | null>] | [];
}

class GithubSync extends EventEmitter<GithubSyncEvents> {
  #paused: ReadableAtom<boolean>;
  #started = false;
  #ctrl = new AbortController();
  #skip = withResolvers<void>();

  oid?: string;
  #getGithubToken: GetGithubToken;

  constructor(
    public interrupted: ReadableAtom<boolean>,
    paused: ReadableAtom<boolean>,
    getGithubToken: GetGithubToken,
  ) {
    super();
    this.#paused = paused;
    this.#getGithubToken = getGithubToken;
  }

  abort() {
    this.#ctrl.abort();
  }

  #aborted() {
    return this.#ctrl.signal.aborted;
  }

  update(oid: string) {
    this.oid = oid;

    if (import.meta.env.DEV && !this.interrupted.get()) {
      unreachable('the expectation is that the oid is overwritten by processes that pause the sync first');
    }
  }

  skipWait() {
    this.#skip.resolve();
    this.#skip = withResolvers();
  }

  async start(oid: string, target: Target) {
    this.oid = oid;

    if (this.#started) {
      if (import.meta.env.DEV) {
        unreachable('start() should only be called once');
      }

      return;
    }

    this.#started = true;

    let lastFetch = null as null | number;

    const paused = computed([this.#paused, this.interrupted], (paused, interrupted) => paused || interrupted);

    while (!this.#aborted()) {
      await waitUntil(paused, false, { signal: this.#ctrl.signal }).catch(NOOP);

      if (this.#aborted()) {
        return;
      }

      const timeSinceLastFetch = Date.now() - (lastFetch ?? Date.now());

      if (timeSinceLastFetch < POLL_INTERVAL) {
        const controller = new AbortController();

        wait(POLL_INTERVAL - timeSinceLastFetch).then(() => controller.abort());

        const interruptedPromise = waitUntil(this.interrupted, true, { signal: controller.signal }).then(
          () => true,
          () => false,
        );

        const interrupted = await Promise.race([interruptedPromise, this.#skip.promise]);

        if (interrupted) {
          continue;
        }
      }

      if (this.#aborted()) {
        return;
      }

      lastFetch = Date.now();

      logger.debug('pulling contents from remote');

      await this.pullAndDispatch(target);
    }
  }

  async pullAndDispatch(target: Target, interruptible = true) {
    const result = await this.#pull(target, interruptible);

    if (result?.oid != null) {
      logger.debug('pulled', result.oid, 'dispatching update');

      this.dispatch('update', [{ current: result.oid, previous: result.previousOid }, result.stream]);
    } else if (result != null) {
      logger.debug('pulled', 'empty update');

      this.dispatch('update', []);
    }
  }

  async #pull(target: Target, interruptible = true) {
    if (!this.oid) {
      unreachable('should be guarded');
    }

    const interrupted = () => {
      if (interruptible && (this.#aborted() || this.interrupted.get())) {
        logger.debug('pull interrupted');

        return true;
      }

      return false;
    };

    const token = await this.#getGithubToken();

    if (token == null) {
      return undefined;
    }

    const reference = await getReference(target, token);

    const oid = reference?.target?.oid;
    const previousOid = this.oid;

    if (interrupted()) {
      return undefined;
    }

    if (!oid || previousOid === oid) {
      logger.debug('pull with no effect', previousOid, oid);

      return { oid: undefined };
    }

    // we want to cancel the whole thing on destruction _or_ interruption
    const controller = new AbortController();
    const signal = interruptible ? AbortSignal.any([controller.signal, this.#ctrl.signal]) : this.#ctrl.signal;

    const tarballStream = await filesFromTarball(getTarball(target, oid, token), { signal });

    if (interrupted()) {
      return undefined;
    }

    const [stream, copy] = tarballStream.tee();

    if (interruptible) {
      // cancel on interruption
      waitUntil(this.interrupted, true, { signal })
        .catch(NOOP)
        .finally(() => controller.abort());
    }

    // cleanup on success
    streamDone(copy)
      .catch(NOOP)
      .finally(() => controller.abort());

    return { stream, oid, previousOid };
  }
}

function encode(contents: string, binary?: boolean) {
  return binary ? fromLatin1(contents) : textEncoder.encode(contents);
}

function messageFromChanges(fileChanges: DiffResult<string>) {
  const file = fileChanges.additions.at(0) ?? fileChanges.deletions.at(0);

  invariant(file);

  let change = '';

  if (fileChanges.additions.length > 0 && fileChanges.newFiles.has(file.path)) {
    change = 'Added';
  } else if (fileChanges.additions.length > 0) {
    change = 'Updated';
  } else {
    change = 'Deleted';
  }

  const path = file.path.split('/').at(-1)!;

  const message = `${change} ${path}`;

  return message;
}

async function commitChanges(
  getGithubToken: GetGithubToken,
  { fileChanges, commitMessage }: { fileChanges: DiffResult<string>; commitMessage?: string },
  target: Target,
  oid?: string,
) {
  const token = await getGithubToken();

  if (!token) {
    throw new Error('No GitHub credentials');
  }

  let nextOid: string;

  const commitTarget = {
    repository: target.repository,
    branchName: target.branchName,
  };

  const commit = {
    commitMessage: commitMessage ?? messageFromChanges(fileChanges),
    fileChanges: { additions: fileChanges.additions, deletions: fileChanges.deletions },
  };

  if (oid) {
    const result = await commitFiles({ ...commitTarget, expectedHeadOid: oid }, commit, token);

    if (result == null) {
      return undefined;
    }

    nextOid = result.commit.oid;
  } else {
    nextOid = await createFirstCommit(commitTarget, commit, token);
  }

  return nextOid;
}

async function updateGithubIntegration(projectId: string, data: { branchName: string } | { initialized: boolean }) {
  const response = await clientFetch(`/api/project/integrations/github/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Unable to update GitHub project integration data');
  }
}

type MaybeAsyncIterable<T> = AsyncIterable<T> | Iterable<T>;

class GithubCloneError extends Error {
  name = 'GithubCloneError';

  constructor(public data: string) {
    super('Github Clone Error');
  }
}

class SerializeMisreportError extends Error {
  name = 'SerializeMisreportError';

  constructor() {
    super('Serialize did complete');
  }
}

function addBreadcrumb(breadcrumb: Omit<Breadcrumb, 'category'>) {
  appsignalAddBreadcrumb({ ...breadcrumb, category: 'github' });
}
